/**
 * Firebase Configuration - SeeTex Web
 * 
 * SECURITY NOTES:
 * - All config values MUST come from environment variables
 * - Never commit actual API keys to version control
 * - Firebase API keys are safe to expose in client-side code ONLY when
 *   combined with proper Firebase Security Rules and App Check
 * - Enable App Check in production for additional security
 */

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
    getAuth,
    setPersistence,
    browserLocalPersistence,
    type Auth,
} from "firebase/auth";
import { getFirestore, type Firestore } from "firebase/firestore";

// Validate required environment variables
const requiredEnvVars = [
    "VITE_FIREBASE_API_KEY",
    "VITE_FIREBASE_AUTH_DOMAIN",
    "VITE_FIREBASE_PROJECT_ID",
] as const;

function validateEnvVars(): void {
    const missing = requiredEnvVars.filter(
        (key) => !import.meta.env[key]
    );

    if (missing.length > 0 && import.meta.env.PROD) {
        throw new Error(
            `Missing required Firebase environment variables: ${missing.join(", ")}. ` +
            "Please configure your .env file."
        );
    }
}

// Firebase configuration from environment variables
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "",
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "",
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "",
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "",
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "",
    appId: import.meta.env.VITE_FIREBASE_APP_ID || "",
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;

/**
 * Initialize Firebase with security best practices
 * - Uses local persistence (survives browser refresh)
 * - Validates configuration before initialization
 */
export function initializeFirebase(): { app: FirebaseApp; auth: Auth } {
    if (app && auth) {
        return { app, auth };
    }

    // In development, allow running without Firebase (demo mode)
    const isDemoMode = import.meta.env.DEV && !import.meta.env.VITE_FIREBASE_API_KEY;

    if (isDemoMode) {
        if (import.meta.env.DEV) console.warn(
            "[SeeTex Auth] Running in demo mode - Firebase not configured. " +
            "Copy .env.example to .env and add your Firebase credentials."
        );
        // Return mock objects for demo mode
        return {
            app: {} as FirebaseApp,
            auth: {} as Auth,
        };
    }

    validateEnvVars();

    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);

    // Set persistence to LOCAL (persists even when browser is closed)
    // This is secure because Firebase handles token refresh automatically
    setPersistence(auth, browserLocalPersistence).catch((error) => {
        console.error("[SeeTex Auth] Failed to set persistence:", error.code);
    });

    return { app, auth };
}

/**
 * Get the Firebase App instance
 */
export function getFirebaseApp(): FirebaseApp {
    if (!app) {
        const result = initializeFirebase();
        return result.app;
    }
    return app;
}

/**
 * Get the Firestore instance (initializes Firebase if needed)
 */
export function getFirebaseFirestore(): Firestore {
    if (!db) {
        initializeFirebase();
    }
    return db!;
}

/**
 * Get the Firebase Auth instance
 * Throws if Firebase hasn't been initialized
 */
export function getFirebaseAuth(): Auth {
    if (!auth) {
        const result = initializeFirebase();
        return result.auth;
    }
    return auth;
}

/**
 * Check if running in demo mode (no Firebase configured)
 */
export function isDemoMode(): boolean {
    return import.meta.env.DEV && !import.meta.env.VITE_FIREBASE_API_KEY;
}

// Security utility: sanitize user input before sending to Firebase
export function sanitizeEmail(email: string): string {
    return email.trim().toLowerCase();
}

// Security utility: validate email format
export function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

// Security utility: validate password strength
export function validatePassword(password: string): {
    valid: boolean;
    errors: string[];
} {
    const errors: string[] = [];

    if (password.length < 8) {
        errors.push("Password must be at least 8 characters");
    }
    if (!/[A-Z]/.test(password)) {
        errors.push("Password must contain at least one uppercase letter");
    }
    if (!/[a-z]/.test(password)) {
        errors.push("Password must contain at least one lowercase letter");
    }
    if (!/[0-9]/.test(password)) {
        errors.push("Password must contain at least one number");
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
        errors.push("Password must contain at least one special character");
    }

    return {
        valid: errors.length === 0,
        errors,
    };
}
